import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react'
import HaryanaPanchayatPortal from './components/HaryanaPanchayatPortal'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import SchemeDetail from './components/SchemeDetail'
import GramPanchayatPage from './pages/GramPanchayat'
import './index.css'
import { LanguageProvider } from '@/lib/lang'
import { LocationProvider } from '@/contexts/LocationContext'
import { CountersProvider } from '@/contexts/CountersContext'
import { ThemeProvider } from '@/lib/theme'
import ErrorBoundary from '@/components/ErrorBoundary'
import MainLayout from '@/components/layout/MainLayout'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AdminRoute } from '@/components/AdminRoute'

import { ShareTrackingHandler } from '@/components/ShareTrackingHandler'
import { PageTrackingHandler } from '@/components/PageTrackingHandler'
import ScrollToTop from '@/components/ScrollToTop'
import { Toaster } from "@/components/ui/toaster"
import { CookieConsentBanner } from "@/components/CookieConsentBanner"
import { useLanguage } from "@/lib/lang"
import { t } from "@/lib/i18n"
import { useLocationState } from "@/contexts/LocationContext"
import type { InstitutionLevel } from "@/components/InstitutionSelectionPanel"
import { PanchayatSelector } from "@/components/PanchayatSelector"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { trackAnalyticsEvent } from "@/lib/analytics"
import { useAuth } from "@/contexts/AuthContext"
import { useViewerProfile } from "@/hooks/useViewerProfile"
import { getProfileGramPanchayatId } from "@/lib/profile-location"

const PERF_DEBUG_SESSION_ID = "slow-page-load"

function reportPerfDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
  if (!(import.meta as any)?.env?.DEV) return
  if (typeof window === "undefined" || (window as any).__TRAE_PERF_DEBUG__ !== true) return
  if (typeof fetch !== "function") return
  const perfDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
  if (!perfDebugUrl) return
  fetch(perfDebugUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: PERF_DEBUG_SESSION_ID,
      runId: "pre-fix",
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { })
}

type SplashPhase = "idle" | "zoom" | "fade" | "done"

const GeoHiPage = lazy(() => import("./pages/GeoHi"))
const GeoLangPage = lazy(() => import("./pages/GeoLang"))
const GeoHiUploadPage = lazy(() => import("./pages/GeoHiUpload"))
const SchemeAdmin = lazy(() => import("./components/SchemeAdmin"))
const SamvaadVerifyPage = lazy(() => import("./pages/SamvaadVerifyPage"))

const TabMenuPage = lazy(() => import("./pages/TabMenu"))
const SignInPage = lazy(() => import("./pages/SignIn"))
const AuthCallback = lazy(() => import("./pages/AuthCallback"))
const ProfileHomePage = lazy(() => import("./pages/ProfileHomePage"))
const ProfileMyProfilePage = lazy(() => import("./pages/ProfileMyProfilePage"))
const ProfilePage = lazy(() => import("./pages/Profile"))
const MyPostsPage = lazy(() => import("./pages/MyPostsPage"))
const SavedPostsPage = lazy(() => import("./pages/SavedPostsPage"))
const PostDetailsPage = lazy(() => import("./pages/PostDetailsPage"))
const PolicyDocPage = lazy(() => import("./pages/PolicyDocPage"))
const AuthAnalyticsPage = lazy(() => import("./pages/AuthAnalyticsPage"))

const AdminDashboard = lazy(() => import("./pages/AdminDashboard"))
const AdminEngagementAnalytics = lazy(() => import("./pages/AdminEngagementAnalytics"))
const AdminImageEngagementAnalytics = lazy(() => import("./pages/AdminImageEngagementAnalytics"))

const WeatherPage = lazy(() => import("./pages/Weather"))
const OnlineServicesPage = lazy(() => import("./pages/OnlineServices"))
const FarmerServicesPage = lazy(() => import("./pages/FarmerServices"))
const EminentPersonsPage = lazy(() => import("./pages/EminentPersons"))
const RulesRegulationsPage = lazy(() => import("./pages/RulesRegulations"))
const ApplyOnlinePage = lazy(() => import("./pages/ApplyOnline"))
const MyMusicPage = lazy(() => import("./pages/MyMusic"))
const VillageTourPage = lazy(() => import("./pages/VillageTour"))
const VillageHubPage = lazy(() => import("./pages/VillageHub"))
const VillageHubCreatorPage = lazy(() => import("./pages/VillageHubCreator"))
const VoterElectionPage = lazy(() => import("./pages/VoterElection"))
const TravellingServices = lazy(() => import("./pages/TravellingServices"))
const ReelgramRouter = lazy(() => import("./modules/reelgram/ReelgramRouter"))
const Samvaad = lazy(() => import("./components/gram-samvaad/Samvaad"))
const SharePost = lazy(() => import("./components/gram-samvaad/SharePost"))
const SamvaadLoginPage = lazy(() => import("./pages/SamvaadLogin"))
const SamvaadContactsPage = lazy(() => import("./pages/SamvaadContacts"))
const SamvaadSettingsPage = lazy(() => import("./pages/SamvaadSettings"))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
})

const LEGACY_VILLAGE_HUB_PATH = `/${["about", "village"].join("-")}`
const VILLAGE_HUB_PATH = `/${["village", "hub"].join("-")}`

function LegacyVillageHubRedirect() {
  const location = useLocation()
  const legacyTail = new RegExp(`${LEGACY_VILLAGE_HUB_PATH}/?$`, "i")
  const nextPathname = location.pathname.replace(legacyTail, VILLAGE_HUB_PATH)
  return <Navigate to={{ pathname: nextPathname, search: location.search }} replace />
}

function parseInstitution(search: string, ctx: { selectedDistrict: string; selectedBlock: string; selectedVillage: string }) {
  const sp = new URLSearchParams(search)
  const rawLevel = (sp.get("level") || "").toLowerCase()
  const district = sp.get("district") || ctx.selectedDistrict || ""
  const block = sp.get("block") || ctx.selectedBlock || ""
  const village = sp.get("village") || ctx.selectedVillage || ""

  const level: InstitutionLevel =
    rawLevel === "gp" || rawLevel === "bs" || rawLevel === "zp" ? (rawLevel as InstitutionLevel) : "gp"

  const isValid =
    (level === "zp" && !!district) ||
    (level === "bs" && !!district && !!block) ||
    (level === "gp" && !!district && !!block && !!village)

  return { level, district, block, village, isValid, sp }
}

export function InstitutionRequired({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { lang } = useLanguage()
  const { currentUser } = useAuth()
  const { selectedDistrict, selectedBlock, selectedVillage, setSelectedDistrict, setSelectedBlock, setSelectedVillage } = useLocationState()
  const [profileSyncStatus, setProfileSyncStatus] = useState<"idle" | "checking" | "done">("idle")

  const parsed = useMemo(() => {
    return parseInstitution(location.search, { selectedDistrict, selectedBlock, selectedVillage })
  }, [location.search, selectedBlock, selectedDistrict, selectedVillage])

  useEffect(() => {
    const urlDistrict = parsed.sp.get("district")
    const urlBlock = parsed.sp.get("block")
    const urlVillage = parsed.sp.get("village")
    if (urlDistrict && urlDistrict !== selectedDistrict) setSelectedDistrict(urlDistrict)
    if (urlBlock && urlBlock !== selectedBlock) setSelectedBlock(urlBlock)
    if (urlVillage && urlVillage !== selectedVillage) setSelectedVillage(urlVillage)
  }, [parsed.sp, selectedBlock, selectedDistrict, selectedVillage, setSelectedBlock, setSelectedDistrict, setSelectedVillage])

  const urlHasDistrict = !!parsed.sp.get("district")
  const urlHasBlock = !!parsed.sp.get("block")
  const urlHasVillage = !!parsed.sp.get("village")
  const urlHasLevel = !!parsed.sp.get("level")

  const urlIsValid =
    (parsed.level === "zp" && urlHasLevel && urlHasDistrict) ||
    (parsed.level === "bs" && urlHasLevel && urlHasDistrict && urlHasBlock) ||
    (parsed.level === "gp" && urlHasLevel && urlHasDistrict && urlHasBlock && urlHasVillage)

  const ctxIsValid = !!selectedDistrict && !!selectedBlock && !!selectedVillage
  const initialCtxValidRef = useRef(ctxIsValid)
  const viewerProfileQuery = useViewerProfile({
    enabled: !urlIsValid && !ctxIsValid,
  })

  useEffect(() => {
    if (urlIsValid) return
    if (ctxIsValid) return
    if (!currentUser?.id) return
    if (viewerProfileQuery.isLoading) {
      setProfileSyncStatus("checking")
      return
    }
    const row: any = viewerProfileQuery.data
    const d = String(row?.district || "").trim()
    const b = String(row?.block || "").trim()
    const v = getProfileGramPanchayatId(row)
    if (!d || !b || !v) {
      setProfileSyncStatus("done")
      return
    }

    setProfileSyncStatus("checking")
    if (d !== selectedDistrict) setSelectedDistrict(d)
    if (b !== selectedBlock) setSelectedBlock(b)
    if (v !== selectedVillage) setSelectedVillage(v)

    const sp = new URLSearchParams(location.search)
    sp.set("level", "gp")
    sp.set("district", d)
    sp.set("block", b)
    sp.set("village", v)
    navigate({ pathname: location.pathname, search: `?${sp.toString()}` }, { replace: true })
    setProfileSyncStatus("done")
  }, [
    ctxIsValid,
    currentUser?.id,
    location.pathname,
    location.search,
    navigate,
    selectedBlock,
    selectedDistrict,
    selectedVillage,
    setSelectedBlock,
    setSelectedDistrict,
    setSelectedVillage,
    urlIsValid,
    viewerProfileQuery.data,
    viewerProfileQuery.isLoading,
  ])

  useEffect(() => {
    if (urlIsValid) return
    if (!ctxIsValid) return

    const sp = new URLSearchParams(location.search)
    sp.set("level", "gp")
    sp.set("district", selectedDistrict)
    sp.set("block", selectedBlock)
    sp.set("village", selectedVillage)

    if (!initialCtxValidRef.current) {
      void trackAnalyticsEvent("institution_selected", {
        level: "gp",
        district: selectedDistrict,
        block: selectedBlock,
        village: selectedVillage,
        source: "route_guard",
        path: location.pathname,
      }).catch(() => void 0)
    }

    navigate({ pathname: location.pathname, search: `?${sp.toString()}` }, { replace: true })
  }, [ctxIsValid, location.pathname, location.search, navigate, selectedBlock, selectedDistrict, selectedVillage, urlIsValid])

  if (!urlIsValid && !ctxIsValid && currentUser?.id && profileSyncStatus === "checking") {
    return (
      <Dialog open>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-xl p-6 overflow-hidden">
          <DialogHeader>
            <DialogTitle>{t("loading", lang) || "Loading"}</DialogTitle>
            <DialogDescription>{t("loading_profile", lang) || "Syncing your profile details..."}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    )
  }

  if (urlIsValid) return <>{children}</>

  return (
    <Dialog open>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl p-0 overflow-hidden">
        <div className="p-6 border-b">
          <DialogHeader>
            <DialogTitle>{t("select_village", lang)}</DialogTitle>
            <DialogDescription>{t("first_select_institution", lang)}</DialogDescription>
          </DialogHeader>
        </div>
        <div className="p-6">
          <PanchayatSelector mode="persistent" storageKey="institution_session_selection" />
        </div>
      </DialogContent>
    </Dialog>
  )
}

function getWelcomeTiming(prefersReducedMotion: boolean) {
  if (prefersReducedMotion) {
    return {
      zoomMs: 0,
      fadeMs: 0,
      scale: 1,
    }
  }

  return {
    zoomMs: 900,
    fadeMs: 260,
    scale: 1,
  }
}

export function getAutoSplashTiming(opts: { isMobileViewport: boolean; prefersReducedMotion: boolean }) {
  const totalMs = opts.prefersReducedMotion ? 0 : 250
  const fadeMs = 0
  const holdMs = totalMs
  return { holdMs, fadeMs, totalMs }
}

function useReducedMotionPreference() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
    if (!mediaQuery || typeof (mediaQuery as any).matches !== "boolean") return
    const update = () => setPrefersReducedMotion(mediaQuery.matches)

    update()

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", update)
      return () => mediaQuery.removeEventListener("change", update)
    }

    mediaQuery.addListener(update)
    return () => mediaQuery.removeListener(update)
  }, [])

  return prefersReducedMotion
}

function readStoredLanguage(): "en" | "hi" | "pa" | null {
  try {
    const saved = localStorage.getItem("app.lang")
    if (saved !== "en" && saved !== "hi" && saved !== "pa") return null

    const expiresRaw = localStorage.getItem("app.lang.expiresAt")
    if (expiresRaw != null && expiresRaw !== "") {
      const expiresAt = Number(expiresRaw)
      if (!Number.isFinite(expiresAt) || expiresAt <= 0) {
        localStorage.removeItem("app.lang")
        localStorage.removeItem("app.lang.expiresAt")
        return null
      }
      if (Date.now() > expiresAt) {
        localStorage.removeItem("app.lang")
        localStorage.removeItem("app.lang.expiresAt")
        return null
      }
    }

    return saved
  } catch {
    void 0
  }
  return null
}

type SplashMode = "select" | "auto"

function AppSplashScreen({ onDone, mode }: { onDone: () => void; mode: SplashMode }) {
  const prefersReducedMotion = useReducedMotionPreference()
  const [phase, setPhase] = useState<SplashPhase>("idle")
  const timing = useMemo(() => getWelcomeTiming(prefersReducedMotion), [prefersReducedMotion])
  const { setLang } = useLanguage()
  const [langUiReady, setLangUiReady] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") {
      setPhase("done")
      return
    }

    let animationFrame = 0
    let fadeTimer = 0
    let doneTimer = 0
    let langUiTimer = 0
    const splashStartMs = typeof performance !== "undefined" ? performance.now() : Date.now()

    animationFrame = window.requestAnimationFrame(() => setPhase("zoom"))
    // #region debug-point A:splash-mounted
    reportPerfDebug("A", "src/App.tsx:369", "[DEBUG] Splash mounted", {
      mode,
      prefersReducedMotion,
      bootStartMs: (window as any).__slowPageLoadBootStart ?? null,
      splashStartMs,
    })
    // #endregion

    if (mode === "auto") {
      const isMobileViewport = window.innerWidth <= 640
      const { holdMs, fadeMs, totalMs } = getAutoSplashTiming({ isMobileViewport, prefersReducedMotion })
      const startFadeAt = Math.max(0, holdMs)

      if (fadeMs > 0) {
        fadeTimer = window.setTimeout(() => setPhase("fade"), startFadeAt) as unknown as number
      }
      doneTimer = window.setTimeout(() => {
        // #region debug-point A:splash-auto-complete
        reportPerfDebug("A", "src/App.tsx:387", "[DEBUG] Splash auto-complete fired", {
          mode,
          totalMs,
          elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - splashStartMs,
        })
        // #endregion
        setPhase("done")
        onDone()
      }, totalMs) as unknown as number
    } else {
      const delayMs = prefersReducedMotion ? 0 : timing.zoomMs
      langUiTimer = window.setTimeout(() => setLangUiReady(true), Math.max(0, delayMs)) as unknown as number
    }

    return () => {
      window.cancelAnimationFrame(animationFrame)
      if (fadeTimer) window.clearTimeout(fadeTimer)
      if (doneTimer) window.clearTimeout(doneTimer)
      if (langUiTimer) window.clearTimeout(langUiTimer)
    }
  }, [mode, onDone, prefersReducedMotion, timing.zoomMs])

  if (phase === "done") return null

  const persistLang = (next: "en" | "hi" | "pa") => {
    try {
      localStorage.setItem("app.lang", next)
      localStorage.setItem("app.lang.expiresAt", String(Date.now() + 180 * 24 * 60 * 60 * 1000))
    } catch {
      void 0
    }
  }

  const chooseLanguage = (next: "en" | "hi" | "pa") => {
    // #region debug-point A:splash-language-selected
    reportPerfDebug("A", "src/App.tsx:425", "[DEBUG] Splash language selected", {
      next,
      mode,
      elapsedMs: typeof performance !== "undefined" ? performance.now() : Date.now(),
    })
    // #endregion
    persistLang(next)
    setLang(next)

    if (mode === "auto") return

    if (timing.fadeMs <= 0) {
      setPhase("done")
      onDone()
      return
    }

    setPhase("fade")
    window.setTimeout(() => {
      setPhase("done")
      onDone()
    }, timing.fadeMs)
  }

  return (
    <div
      className={`app-splash-screen ${phase === "fade" ? "app-splash-screen--fade" : ""}`}
      style={
        {
          "--splash-zoom-ms": `${mode === "auto" ? (prefersReducedMotion ? 0 : 840) : timing.zoomMs}ms`,
          "--splash-fade-ms": `${mode === "auto" ? (prefersReducedMotion ? 0 : 300) : timing.fadeMs}ms`,
          "--splash-scale": String(timing.scale),
        } as React.CSSProperties
      }
      role="dialog"
      aria-modal="true"
      aria-label={mode === "select" ? "Select language" : "Loading"}
      data-testid="app-splash-screen"
    >
      <div className="app-splash-screen__inner">
        <div
          className={`app-splash-screen__content ${phase !== "idle" ? "app-splash-screen__content--entered" : ""}`}
          data-testid="app-splash-screen-content"
        >
          <img
            src="/images/logodpm.png"
            alt="Digital Panchayat Mission"
            loading="eager"
            decoding="async"
            className="app-splash-screen__logo"
          />
          <div className="app-splash-screen__text">
            <p className="app-splash-screen__tagline">मेरी डिजिटल पंचायत मेरा गौरव</p>
            {mode === "auto" || (mode === "select" && langUiReady) ? (
              <p className="app-splash-screen__prompt" aria-live="polite">अपनी पसंद की भाषा चुने</p>
            ) : null}
          </div>
          {mode === "auto" || (mode === "select" && langUiReady) ? (
            <div className="button-group app-splash-screen__lang-buttons" role="group" aria-label="Language selection">
              <button type="button" className="btn btn-outline" onClick={() => chooseLanguage("en")}>English</button>
              <button type="button" className="btn btn-outline" onClick={() => chooseLanguage("hi")}>हिन्दी</button>
              <button type="button" className="btn btn-outline" onClick={() => chooseLanguage("pa")}>ਪੰਜਾਬੀ</button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function App() {
  const [isSplashComplete, setIsSplashComplete] = useState(false)
  const [splashMode] = useState<SplashMode>(() => (readStoredLanguage() ? "auto" : "select"))

  useEffect(() => {
    // #region debug-point D:app-shell-gate
    reportPerfDebug("D", "src/App.tsx:499", "[DEBUG] App shell gate state changed", {
      isSplashComplete,
      splashMode,
      elapsedSinceBootMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - ((typeof window !== "undefined" ? (window as any).__slowPageLoadBootStart : 0) || 0),
    })
    // #endregion
  }, [isSplashComplete, splashMode])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <LanguageProvider>
          <LocationProvider>
            <CountersProvider>
              {isSplashComplete ? (
                <div className="app-shell app-shell--ready">
                  <ShareTrackingHandler />
                  <PageTrackingHandler />
                  <ScrollToTop />
                  <Toaster />
                  <CookieConsentBanner />
                  <ErrorBoundary>
                    <Suspense fallback={null}>
                      <Routes>
                        <Route element={<MainLayout />}>
                          <Route path="/" element={<HaryanaPanchayatPortal />} />
                          <Route path="/:lang" element={<HaryanaPanchayatPortal />} />
                          <Route path="/gp" element={<GramPanchayatPage />} />
                          <Route path="/:lang/gp" element={<GramPanchayatPage />} />
                          <Route path="/profile" element={<ProfileHomePage />} />
                          <Route path="/:lang/profile" element={<ProfileHomePage />} />
                          <Route path="/profile/my-profile" element={<ProfileMyProfilePage />} />
                          <Route path="/:lang/profile/my-profile" element={<ProfileMyProfilePage />} />
                          <Route path="/profile/my-posts" element={<MyPostsPage />} />
                          <Route path="/:lang/profile/my-posts" element={<MyPostsPage />} />
                          <Route path="/profile/saved-posts" element={<SavedPostsPage />} />
                          <Route path="/:lang/profile/saved-posts" element={<SavedPostsPage />} />
                          <Route path="/profile/settings" element={<SamvaadSettingsPage />} />
                          <Route path="/:lang/profile/settings" element={<SamvaadSettingsPage />} />
                          <Route path="/post/:postId" element={<PostDetailsPage />} />
                          <Route path="/:lang/post/:postId" element={<PostDetailsPage />} />
                          <Route path="/my-posts" element={<MyPostsPage />} />
                          <Route path="/:lang/my-posts" element={<MyPostsPage />} />
                          <Route path="/saved-posts" element={<SavedPostsPage />} />
                          <Route path="/:lang/saved-posts" element={<SavedPostsPage />} />
                          <Route path="/profile/:id" element={<ProfilePage />} />
                          <Route path="/:lang/profile/:id" element={<ProfilePage />} />
                          <Route path="/samvaad/verify" element={<SamvaadVerifyPage />} />
                          <Route path="/:lang/samvaad/verify" element={<SamvaadVerifyPage />} />
                          <Route path="/schemes/:slug" element={<SchemeDetail />} />
                          <Route path="/:lang/schemes/:slug" element={<SchemeDetail />} />
                          <Route path="/policies/:doc" element={<PolicyDocPage />} />
                          <Route path="/:lang/policies/:doc" element={<PolicyDocPage />} />
                          <Route path="/auth-analytics" element={<AuthAnalyticsPage />} />
                          <Route path="/:lang/auth-analytics" element={<AuthAnalyticsPage />} />
                          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                          <Route path="/:lang/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                          <Route path="/admin/engagement" element={<AdminRoute><AdminEngagementAnalytics /></AdminRoute>} />
                          <Route path="/:lang/admin/engagement" element={<AdminRoute><AdminEngagementAnalytics /></AdminRoute>} />
                          <Route path="/admin/engagement/images" element={<AdminRoute><AdminImageEngagementAnalytics /></AdminRoute>} />
                          <Route path="/:lang/admin/engagement/images" element={<AdminRoute><AdminImageEngagementAnalytics /></AdminRoute>} />
                          <Route path="/geo-hi" element={<GeoHiPage />} />
                          <Route path="/geo-hi-upload" element={<GeoHiUploadPage />} />
                          <Route path="/geo" element={<GeoLangPage />} />
                          <Route path="/schemes/:slug/create" element={<SchemeAdmin />} />
                          <Route path="/:lang/schemes/:slug/create" element={<SchemeAdmin />} />
                          <Route path="/schemes/:slug/manage" element={<SchemeAdmin />} />
                          <Route path="/:lang/schemes/:slug/manage" element={<SchemeAdmin />} />
                          <Route path="/gp/menu" element={<TabMenuPage />} />
                          <Route path="/:lang/gp/menu" element={<TabMenuPage />} />
                          <Route path="/sign-in" element={<SignInPage />} />
                          <Route path="/:lang/sign-in" element={<SignInPage />} />
                          <Route path="/auth/callback" element={<AuthCallback />} />
                          <Route
                            path="/my-village-weather"
                            element={
                              <InstitutionRequired>
                                <WeatherPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/my-village-weather"
                            element={
                              <InstitutionRequired>
                                <WeatherPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/online-services"
                            element={<OnlineServicesPage />}
                          />
                          <Route
                            path="/:lang/online-services"
                            element={<OnlineServicesPage />}
                          />
                          <Route path="/farmer-services" element={<FarmerServicesPage />} />
                          <Route path="/:lang/farmer-services" element={<FarmerServicesPage />} />
                          <Route
                            path="/eminent-persons"
                            element={
                              <InstitutionRequired>
                                <EminentPersonsPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/eminent-persons"
                            element={
                              <InstitutionRequired>
                                <EminentPersonsPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route path="/gp/rules" element={<RulesRegulationsPage />} />
                          <Route path="/:lang/gp/rules" element={<RulesRegulationsPage />} />
                          <Route path="/gp/apply" element={<ApplyOnlinePage />} />
                          <Route path="/:lang/gp/apply" element={<ApplyOnlinePage />} />
                          <Route
                            path="/samvaad"
                            element={
                              <InstitutionRequired>
                                <Samvaad />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/samvaad"
                            element={
                              <InstitutionRequired>
                                <Samvaad />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/reelgram/*"
                            element={<ReelgramRouter />}
                          />
                          <Route
                            path="/:lang/reelgram/*"
                            element={<ReelgramRouter />}
                          />
                          <Route
                            path="/my-music"
                            element={<MyMusicPage />}
                          />
                          <Route
                            path="/:lang/my-music"
                            element={<MyMusicPage />}
                          />
                          <Route
                            path="/village-tour"
                            element={
                              <InstitutionRequired>
                                <VillageTourPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/village-tour"
                            element={
                              <InstitutionRequired>
                                <VillageTourPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route path={LEGACY_VILLAGE_HUB_PATH} element={<LegacyVillageHubRedirect />} />
                          <Route path={`/:lang${LEGACY_VILLAGE_HUB_PATH}`} element={<LegacyVillageHubRedirect />} />
                          <Route
                            path="/village-hub"
                            element={
                              <InstitutionRequired>
                                <VillageHubPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/village-hub/creator/:creatorId"
                            element={
                              <InstitutionRequired>
                                <VillageHubCreatorPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/village-hub"
                            element={
                              <InstitutionRequired>
                                <VillageHubPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/village-hub/creator/:creatorId"
                            element={
                              <InstitutionRequired>
                                <VillageHubCreatorPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/voter-election"
                            element={
                              <InstitutionRequired>
                                <VoterElectionPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/voter-election"
                            element={
                              <InstitutionRequired>
                                <VoterElectionPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route path="/travel/*" element={<TravellingServices />} />
                          <Route path="/:lang/travel/*" element={<TravellingServices />} />
                          <Route
                            path="/gram-samvaad"
                            element={
                              <InstitutionRequired>
                                <Samvaad />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/gram-samvaad"
                            element={
                              <InstitutionRequired>
                                <Samvaad />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/samvaad/login"
                            element={
                              <InstitutionRequired>
                                <SamvaadLoginPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/samvaad/login"
                            element={
                              <InstitutionRequired>
                                <SamvaadLoginPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/samvaad/contacts"
                            element={
                              <InstitutionRequired>
                                <SamvaadContactsPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/samvaad/contacts"
                            element={
                              <InstitutionRequired>
                                <SamvaadContactsPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/samvaad/settings"
                            element={
                              <InstitutionRequired>
                                <SamvaadSettingsPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/samvaad/settings"
                            element={
                              <InstitutionRequired>
                                <SamvaadSettingsPage />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/samvaad/c/:id"
                            element={
                              <InstitutionRequired>
                                <Samvaad />
                              </InstitutionRequired>
                            }
                          />
                          <Route
                            path="/:lang/samvaad/c/:id"
                            element={
                              <InstitutionRequired>
                                <Samvaad />
                              </InstitutionRequired>
                            }
                          />
                          <Route path="/share/post/:id" element={<SharePost />} />
                          <Route path="*" element={<HaryanaPanchayatPortal />} />
                        </Route>
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </div>
              ) : (
                <AppSplashScreen mode={splashMode} onDone={() => setIsSplashComplete(true)} />
              )}
            </CountersProvider>
          </LocationProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
